import jexl from 'jexl-extended'
import type Expression from 'jexl/Expression'

/**
 * A twinIdProperty starting with '=' selects a per-object JEXL twin id expression.
 * Plain property names and JSON arrays of names keep the legacy name-based behavior.
 */
export const isTwinIdExpression = (twinIdProperty?: string): twinIdProperty is string =>
  typeof twinIdProperty === 'string' && twinIdProperty.startsWith('=')

const expressionCache: Record<string, Expression> = {}

const stripPrefix = (expression: string): string =>
  expression.startsWith('=') ? expression.substring(1) : expression

/**
 * Compile-once evaluation against a context. Returns undefined when the expression
 * cannot be compiled or evaluated — never the raw expression text, so a broken
 * expression can never be mistaken for a twin id.
 */
const evalExpression = (expression: string, context: Record<string, unknown>): unknown => {
  const src = stripPrefix(expression)
  try {
    const expr = expressionCache[src] ?? jexl.createExpression(src)
    expressionCache[src] = expr
    return expr.evalSync(context)
  } catch {
    return undefined
  }
}

/**
 * Extract the literal property keys referenced as properties['Key'] / properties["Key"]
 * so only those keys need to be fetched from the property db.
 * Returns an empty array when the expression has no literal lookups (constants,
 * computed keys) — callers should then fall back to fetching all properties.
 */
export const extractTwinIdPropertyKeys = (expression: string): string[] => {
  const src = stripPrefix(expression)
  const keys: string[] = []
  const pattern = /properties\[\s*'([^']*)'\s*\]|properties\[\s*"([^"]*)"\s*\]/g
  let match: RegExpExecArray | null = pattern.exec(src)
  while (match !== null) {
    const key = match[1] ?? match[2]
    if (key.length > 0 && !keys.includes(key)) keys.push(key)
    match = pattern.exec(src)
  }
  return keys
}

/** Matches 'undefined'/'null' as standalone tokens, treating '_' and other
 * non-alphanumerics as boundaries so ids like 'undefined_Y' are caught. */
const BARE_MISSING_TOKEN = /(^|[^A-Za-z0-9])(undefined|null)([^A-Za-z0-9]|$)/

/**
 * Evaluate a twin id expression against a map of the object's properties
 * (keyed by displayName). Returns undefined when the object must not be mapped:
 * nullish results, object/array results, empty results, or results containing
 * bare 'undefined'/'null' tokens (the signature of a missing property).
 */
export const evaluateTwinIdExpression = (
  expression: string,
  properties: Record<string, unknown>
): string | undefined => {
  const raw = evalExpression(expression, { properties })
  if (raw === undefined || raw === null) return undefined
  if (typeof raw === 'object') {
    console.warn(
      `[twin-id-expression] expression returned an object instead of a string: ${stripPrefix(expression)}`
    )
    return undefined
  }
  const value = String(raw)
  if (value.trim().length === 0) return undefined
  if (BARE_MISSING_TOKEN.test(value)) return undefined
  return value
}
